import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { userService, UserData, UserUpdateData } from '../../api/userService';
import { theme } from '../../styles/theme';
import Button from '../../components/UI/Button';

const EditProfileContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: ${theme.spacing.lg};
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: ${theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${theme.colors.gray};
  margin-bottom: ${theme.spacing.lg};
`;

const Form = styled.form`
  background-color: white;
  border-radius: ${theme.radii.medium};
  box-shadow: ${theme.shadows.small};
  padding: ${theme.spacing.lg};
`;

const FormSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.large};
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.light};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.xs};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.radii.small};
  font-size: ${theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.radii.small};
  font-size: ${theme.fontSizes.medium};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.danger};
  background-color: #fde8e8;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.radii.small};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.fontSizes.small};
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  font-size: ${theme.fontSizes.large};
`;

const AvatarPreview = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${theme.colors.light};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: ${theme.spacing.md};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 50px;
    height: 50px;
    color: ${theme.colors.primary};
  }
`;

const ProfileEdit: React.FC = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const {
    data: profileResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => userService.getCurrentUser(),
  });
  
  // Create form state
  const [formData, setFormData] = useState<UserUpdateData>({
    first_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    avatar_url: '',
    phone_number: '',
    website: '',
    company: '',
    job_title: '',
  });
  
  const [formError, setFormError] = useState<string | null>(null);
  
  // Load user data into form when available
  useEffect(() => {
    // Log the response to help with debugging
    console.log('Profile response received:', profileResponse);
    
    // Handle different response structures
    const userData = 
      (profileResponse as any)?.user || 
      (profileResponse as any)?.data?.user || 
      authUser;
    
    console.log('Extracted user data:', userData);
    
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        display_name: userData.display_name || userData.name || '',
        bio: userData.bio || '',
        avatar_url: userData.avatar_url || '',
        phone_number: userData.phone_number || '',
        website: userData.website || '',
        company: userData.company || '',
        job_title: userData.job_title || '',
      });
    } else {
      console.warn('No user data available to populate form');
    }
  }, [profileResponse, authUser]);
  
  // Update profile mutation
  const mutation = useMutation({
    mutationFn: (data: UserUpdateData) => userService.updateProfile(data),
    onSuccess: (response) => {
      // Log the response to help with debugging
      console.log('Profile update success response:', response);
      // Invalidate the user profile query to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      // Navigate back to the profile view
      navigate('/profile');
    },
    onError: (error: any) => {
      console.error('Error updating profile:', error);
      
      // Log detailed error information
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      // Set error message with more detail if available
      setFormError(
        error?.response?.data?.message || 
        error?.response?.data?.error || 
        error?.message || 
        'Failed to update profile. Please try again.'
      );
    },
  });
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    mutation.mutate(formData);
  };
  
  // Handle cancellation
  const handleCancel = () => {
    navigate('/profile');
  };
  
  if (isLoading) {
    return <LoadingContainer>Loading profile...</LoadingContainer>;
  }
  
  return (
    <EditProfileContainer>
      <PageHeader>
        <Title>Edit Profile</Title>
        <Subtitle>Update your personal information and profile settings</Subtitle>
      </PageHeader>
      
      <Form onSubmit={handleSubmit}>
        {(formError || error) && (
          <ErrorMessage>
            {formError || 'Error loading profile. Please try again later.'}
          </ErrorMessage>
        )}
        
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="avatar_url">Profile Picture URL</Label>
            <Input
              id="avatar_url"
              name="avatar_url"
              type="text"
              value={formData.avatar_url}
              onChange={handleChange}
              placeholder="Enter URL to your profile picture"
            />
            {formData.avatar_url && (
              <AvatarPreview>
                <img src={formData.avatar_url} alt="Avatar preview" />
              </AvatarPreview>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Enter your first name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Enter your last name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              name="display_name"
              type="text"
              value={formData.display_name}
              onChange={handleChange}
              placeholder="Enter your display name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="bio">Bio</Label>
            <TextArea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Tell us about yourself"
              rows={4}
            />
          </FormGroup>
        </FormSection>
        
        <FormSection>
          <SectionTitle>Contact Information</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="phone_number">Phone Number</Label>
            <Input
              id="phone_number"
              name="phone_number"
              type="text"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="Enter your phone number"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              name="website"
              type="text"
              value={formData.website}
              onChange={handleChange}
              placeholder="Enter your website URL"
            />
          </FormGroup>
        </FormSection>
        
        <FormSection>
          <SectionTitle>Professional Information</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              placeholder="Enter your company name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="job_title">Job Title</Label>
            <Input
              id="job_title"
              name="job_title"
              type="text"
              value={formData.job_title}
              onChange={handleChange}
              placeholder="Enter your job title"
            />
          </FormGroup>
        </FormSection>
        
        <ButtonGroup>
          <Button 
            type="button" 
            onClick={handleCancel}
            variant="outline"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </ButtonGroup>
      </Form>
    </EditProfileContainer>
  );
};

export default ProfileEdit; 